import { motion } from 'framer-motion';
import { useState } from 'react';
import Button from '../../../components/base/Button';

interface PopupData {
  title: string;
  description: string;
  details: string[];
}

export default function PresidentMessage() {
  const [selectedPopup, setSelectedPopup] = useState<PopupData | null>(null);

  const popupData: Record<string, PopupData> = {
    trust: {
      title: "信頼",
      description: "59年の実績が築いた確かな信頼関係",
      details: [
        "1965年創業以来、一度も途切れることなく続く地域企業様との長期パートナーシップ",
        "親子3世代にわたってお付き合いいただいているクライアント様も多数",
        "創業時からの理念「お客様第一」を貫き通してきた企業姿勢",
        "業界団体や地域コミュニティでの積極的な活動による社会貢献",
        "従業員一人一人が大切にする「約束は必ず守る」という企業文化"
      ]
    },
    innovation: {
      title: "革新",
      description: "最新技術と伝統的な手法の融合による革新的なアプローチ",
      details: [
        "AI・機械学習を活用した効果測定と最適化システムの導入",
        "VR・ARを使った体験型広告コンテンツの制作技術",
        "デジタルサイネージとスマートフォンを連携させた新しい広告体験",
        "環境に配慮したサステナブルな広告制作手法の開発"
      ]
    },
    growth: {
      title: "成長",
      description: "お客様と共に歩み続ける持続的な成長パートナーシップ",
      details: [
        "クライアント様の事業拡大に合わせた柔軟なサービス展開",
        "新規事業立ち上げから成熟期まで、各フェーズに最適な支援を提供",
        "従業員のスキルアップ投資により、常に最新の知識と技術を保持",
        "グループ企業との連携により、建設から広告まで事業領域を拡大",
        "地域密着から全国展開まで、お客様の成長ステージに応じた対応力",
        "持続可能な経営を通じて、次世代に続く価値創造を実現"
      ]
    }
  };

  const openPopup = (key: string) => {
    setSelectedPopup(popupData[key]);
  };

  const closePopup = () => {
    setSelectedPopup(null);
  };

  return (
    <section id="president" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">社長メッセージ</h2>
          <p className="text-xl text-gray-600">MESSAGE FROM THE PRESIDENT</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <img 
              src="https://static.readdy.ai/image/c0a14dc189bb3eb0b6d6716d0af92e52/3f3e7b5b4c51b85d8bf727ae53541b2d.png"
              alt="代表取締役 近藤和裕"
              className="rounded-2xl shadow-lg w-full h-[600px] object-cover object-center"
            />
            <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2">
              <p className="font-bold text-gray-900">代表取締役</p>
              <p className="text-blue-600 font-semibold">近藤 和裕</p>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6 h-full"
          >
            <div className="bg-blue-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                お客様の想いを形にする<br />
                パートナーとして
              </h3>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  創業から59年、株式会社バンは群馬の地で多くの企業様とともに歩んでまいりました。時代は大きく変わり、広告・マーケティングの手法も劇的に進化していますが、私たちが大切にしている想いは変わりません。
                </p>
                
                <p>
                  それは、お客様の想いを深く理解し、それを最適な形で表現することです。単なる広告制作会社ではなく、お客様のビジネスパートナーとして、共に成長していくことを目指しています。
                </p>
                
                <p>
                  新日東グループの一員として、建設・施工から広告・マーケティングまで、グループの総合力を活かしたワンストップサービスをご提供できることも、私たちの大きな強みです。
                </p>
                
                <p>
                  AIやデジタル技術の進歩により、広告業界も新たな局面を迎えています。私たちは最新技術を積極的に取り入れながらも、人と人とのつながりを大切にした、心に響く広告づくりを続けてまいります。
                </p>
                
                <p>
                  今後とも、お客様の信頼にお応えし、地域とともに発展していく企業として努力を重ねてまいります。どうぞよろしくお願いいたします。
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 text-center">
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-white rounded-xl p-4 shadow-lg border border-gray-100 cursor-pointer"
                onClick={() => openPopup('trust')}
              >
                <div className="text-2xl font-bold text-blue-600 mb-1">信頼</div>
                <div className="text-xs text-gray-600">59年の実績</div>
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-white rounded-xl p-4 shadow-lg border border-gray-100 cursor-pointer"
                onClick={() => openPopup('innovation')}
              >
                <div className="text-2xl font-bold text-blue-600 mb-1">革新</div>
                <div className="text-xs text-gray-600">最新技術導入</div>
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-white rounded-xl p-4 shadow-lg border border-gray-100 cursor-pointer"
                onClick={() => openPopup('growth')}
              >
                <div className="text-2xl font-bold text-blue-600 mb-1">成長</div>
                <div className="text-xs text-gray-600">共に発展</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ポップアップモーダル */}
      {selectedPopup && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closePopup}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-3xl font-bold text-blue-600 mb-2">
                  {selectedPopup.title}
                </h3>
                <p className="text-lg text-gray-700 font-medium">
                  {selectedPopup.description}
                </p>
              </div>
              <button
                onClick={closePopup}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              {selectedPopup.details.map((detail, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start space-x-3"
                >
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-blue-600 text-sm"></i>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{detail}</p>
                </motion.div>
              ))}
            </div>

            <div className="mt-8 flex justify-center">
              <Button onClick={closePopup}>
                閉じる
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
}
